import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Info } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/motion";
import { Process } from "../components/Process";
import { QuoteCTA } from "../components/QuoteCTA";
import { SERVICES } from "../lib/services";

const Index = () => (
  <nav aria-label="Services index" className="sticky top-[72px] z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl" data-testid="services-index">
    <div className="mx-auto flex max-w-[1400px] gap-2 overflow-x-auto px-5 py-3 md:px-10">
      {SERVICES.map((s) => (
        <a key={s.slug} href={`#${s.slug}`} data-testid={`services-index-${s.slug}`} className="shrink-0 rounded-full border border-slate-300 px-4 py-2 text-sm text-ink transition-colors hover:border-ink hover:bg-ink hover:text-white">
          <span className="mr-2 font-mono text-xs text-emerald-700">{s.no}</span>{s.nav}
        </a>
      ))}
    </div>
  </nav>
);

const Block = ({ s, i }) => (
  <section id={s.slug} className={`scroll-mt-40 py-20 md:py-28 ${i % 2 ? "bg-porcelain" : "bg-white"}`} data-testid={`service-section-${s.slug}`}>
    <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 md:px-10 lg:grid-cols-12 lg:gap-20">
      <Reveal className={`lg:col-span-6 ${i % 2 ? "lg:order-2" : ""}`}>
        <div className={`${i % 2 ? "clip-cut" : "clip-cut-alt"} relative h-[320px] overflow-hidden md:h-[480px]`}>
          <img src={s.image} alt={s.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] hover:scale-105" />
          <span className="absolute left-5 top-5 rounded-full bg-ink/75 px-3 py-1.5 font-mono text-xs text-glint backdrop-blur">{s.no} / 06</span>
        </div>
      </Reveal>
      <div className="lg:col-span-6">
        <Reveal><Eyebrow>{s.kicker}</Eyebrow></Reveal>
        <Reveal delay={0.06}><h2 className="mt-5 font-display text-3xl font-semibold leading-[1.05] tracking-tight text-ink md:text-5xl">{s.nav}</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-5 text-base leading-relaxed text-slate-600 md:text-lg">{s.intro}</p></Reveal>
        {s.slug === "deep-cleaning-floor-care" && (
          <Reveal delay={0.12}>
            <p className="mt-5 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="deep-clean-availability-note">
              <Info className="mt-0.5 h-4 w-4 shrink-0" /> Offered where appropriately qualified providers are available, primarily as a periodic add-on to recurring contracts.
            </p>
          </Reveal>
        )}
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          {s.scope.slice(0, 4).map((x) => (
            <li key={x} className="flex items-start gap-3 text-sm text-ink md:text-base"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{x}</li>
          ))}
        </ul>
        <div className="mt-6 flex flex-wrap gap-2">
          {s.suited.map((x) => <span key={x} className="rounded-full bg-ink/5 px-3 py-1.5 text-xs text-slate-700">{x}</span>)}
        </div>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <Link to={s.path} data-testid={`services-detail-${s.slug}`} className="btn-ink group">View full scope <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></Link>
          <Link to="/request-quote" data-testid={`services-quote-${s.slug}`} className="btn-outline-ink group">Request a Free Cleaning Quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
        </div>
      </div>
    </div>
  </section>
);

export default function Services() {
  return (
    <>
      <Seo title="Commercial Cleaning Services West Valley AZ | Shine Solutions" description="Recurring office cleaning, janitorial, retail, medical and dental, common area, deep cleaning and floor care in Peoria, Glendale and Surprise, AZ. Free quotes." path="/services" />
      <PageHero kicker="Our services" lines={["Commercial cleaning,", "built around", "recurring service."]} intro="Six distinct services for offices, retail spaces, healthcare facilities and commercial properties. Each is delivered under a recurring contract with a written scope — not as a one-time job." />
      <Index />
      {SERVICES.map((s, i) => <Block key={s.slug} s={s} i={i} />)}
      <Process />
      <QuoteCTA />
    </>
  );
}
